import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

const IDLE_LIMIT = 30 * 60 * 1000; // 30 minuti di inattività (solo browser, non PWA)
const LAST_KEY = "pc_last_activity";
const isStandalone = typeof window !== "undefined" && ((window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) || window.navigator.standalone === true);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = auth

  const doLogout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    localStorage.removeItem(LAST_KEY);
    setUser(false);
  }, []);

  // Controllo sessione all'avvio (con timeout di inattività persistente tra reload/tab).
  // In PWA (standalone) NON si applica il timeout: accesso una sola volta, sessione mantenuta.
  useEffect(() => {
    const last = parseInt(localStorage.getItem(LAST_KEY) || "0", 10);
    if (!isStandalone && last && Date.now() - last > IDLE_LIMIT) {
      doLogout();
      return;
    }
    api
      .get("/auth/me")
      .then((res) => { setUser(res.data); localStorage.setItem(LAST_KEY, String(Date.now())); })
      .catch(() => setUser(false));
  }, [doLogout]);

  // Tracciamento attività + controllo inattività (disattivato in PWA standalone)
  useEffect(() => {
    if (!user) return;
    const onForcedLogout = () => { localStorage.removeItem(LAST_KEY); setUser(false); };
    window.addEventListener("auth:logout", onForcedLogout);
    if (isStandalone) {
      return () => window.removeEventListener("auth:logout", onForcedLogout);
    }
    localStorage.setItem(LAST_KEY, String(Date.now()));
    let throttle = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - throttle > 5000) { throttle = now; localStorage.setItem(LAST_KEY, String(now)); }
    };
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    const interval = setInterval(() => {
      const last = parseInt(localStorage.getItem(LAST_KEY) || "0", 10);
      if (last && Date.now() - last > IDLE_LIMIT) {
        toast.info("Sessione scaduta per inattività. Accedi di nuovo.");
        doLogout();
      }
    }, 30000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      clearInterval(interval);
      window.removeEventListener("auth:logout", onForcedLogout);
    };
  }, [user, doLogout]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem(LAST_KEY, String(Date.now()));
    setUser(data);
    return data;
  };

  const logout = async () => { await doLogout(); };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export { formatApiErrorDetail };
